import React from 'react';
import { Link } from 'react-router-dom';

import { BASE, PRICES } from '../../captivision.config';
import { CVHeading } from '../../document.component';
import { UI, Jump, Rows } from '../../rail-document.component';
import { CVSubnav, CVNext, CVFigs, Shot } from '../../shell.component';
import { formatPrice } from '../../i18n/language.component';

// The full guide, in English. Every other guide.<language>.jsx here is the same
// page translated: keep their structure - headings, ids, screenshots - in step
// with this one. <UI> names a control the way the app labels it in that
// language.

const monthly = formatPrice('en', PRICES.monthly);
const yearly = formatPrice('en', PRICES.yearly);

const page = {
    title: 'User Guide',
    railLabel: 'Guide contents',
    contents: [
        {
            group: 'Basics',
            links: [
                { id: 'overview', label: 'How CaptiVision works' },
                { id: 'requirements', label: 'What you need' }
            ]
        },
        {
            group: 'On the iPad or Mac',
            links: [
                { id: 'session', label: 'Starting a session' },
                { id: 'bar', label: 'The top bar' },
                { id: 'cameras', label: 'Adding cameras' },
                { id: 'views', label: 'Choosing a view' },
                { id: 'speaker', label: 'Who is speaking' },
                { id: 'names', label: 'Naming people' },
                { id: 'pin', label: 'Pointing out a speaker' },
                { id: 'captions', label: 'Captions and speech' },
                { id: 'tiles', label: 'Controlling a camera' },
                { id: 'recording', label: 'Recording' },
                { id: 'log', label: 'Session log' },
                { id: 'leave', label: 'Ending a session' }
            ]
        },
        {
            group: 'On the iPhone',
            links: [
                { id: 'join', label: 'Joining a session' },
                { id: 'screen', label: 'Reading the camera screen' },
                { id: 'framing', label: 'Framing the shot' },
                { id: 'cool', label: 'Staying cool' },
                { id: 'interpreter', label: 'Interpreter camera' },
                { id: 'owner', label: "For the phone's owner" }
            ]
        },
        {
            group: 'Help',
            links: [
                { id: 'trouble', label: 'Troubleshooting' },
                { id: 'pro', label: 'CaptiVision Pro' },
                { id: 'access', label: 'Languages and accessibility' }
            ]
        }
    ],
    head: <>
        <span className="cv-eyebrow"> User guide </span>
        <h1> Full guide </h1>
        <p className="cv-lede">
            Everything CaptiVision Host and CaptiVision Client can do, and how to use it. New to
            CaptiVision? Start with <Link to={`${BASE}/get-started`}>Get started</Link>.
        </p>
        <CVSubnav current="guide" />
    </>,
    body: <>
        {/* Basics */}
        <CVHeading id="overview" num="01"> How CaptiVision works </CVHeading>
        <p>
            CaptiVision uses two apps. <strong>CaptiVision Client</strong> runs on iPhones placed around the room. Each
            phone films and listens, and sends its picture and sound to the iPad or Mac over your Wi-Fi.
        </p>
        <p>
            <strong>CaptiVision Host</strong> runs on the iPad or Mac. It finds the faces on every camera, and for each face it
            checks whether that person's lips match the sound it hears. That is how it tells the person speaking from
            someone who is nodding, smiling or chewing. It then outlines the speaker and writes live captions with their
            name in front.
        </p>
        <p>
            All of this happens on the iPad or Mac. Nothing is sent to the internet, so it also works where there is no
            internet at all.
        </p>

        <CVHeading id="requirements" num="02"> What you need </CVHeading>
        <Rows rows={[
            ['iPad or Mac', 'An iPad with iPadOS 17 or later, or a Mac with Apple silicon and macOS 14 or later, with CaptiVision Host. Keep it plugged in for long sessions.'],
            ['iPhones', 'iOS 15.6 or later, with CaptiVision Client. Up to six cameras on your own Wi-Fi, or four on a shared building network.'],
            ['Wi-Fi', <>All devices on the same network. Some guest networks keep devices from seeing each other; see <Jump to="trouble">Troubleshooting</Jump>.</>],
            ['Stands', 'A small tripod or stand for each phone.']
        ]} />

        {/* Host */}
        <span className="cv-part"> On the iPad or Mac: CaptiVision Host </span>

        <CVHeading id="session" num="03"> Starting a session </CVHeading>
        <p>
            Open CaptiVision Host, type a <UI>Session name</UI> and tap <UI>Start session</UI>.
            The name is what the phones see when they look for a session, so make it easy to recognise, such as
            "Room 204 Science".
        </p>
        <p>
            The first time, a short tour points at each button in the top bar. Tap <UI>?</UI> at any time
            to see it again.
        </p>
        <p className="cv-tip"><b>On a Mac</b>CaptiVision Host is the same app on a Mac with Apple silicon. Wherever this guide says tap, click. Where it says long-press a tile, right-click it.</p>

        <CVHeading id="bar" num="04"> The top bar </CVHeading>
        <p>Every control you need during a session sits in one bar along the top of the screen.</p>
        <Rows head={['Control', 'What it does']} rows={[
            ['Grid, Auto, Two-Feed', <>Choose what the room sees. See <Jump to="views">Choosing a view</Jump>.</>],
            ['?', 'Replays the tour of the controls.'],
            ['Speakers', 'Everyone who has spoken, with a photo. Name people here.'],
            ['Captions', 'Shows or hides captions, and sets their size and how many lines stay on screen.'],
            ['Speech engine', 'Chooses and checks the engine that writes captions. Shows a warning mark when it needs setting up.'],
            ['Session log', 'Every join, drop, recording and action, with the time.'],
            ['Add camera', 'Shows the pairing code so a phone can join.'],
            ['Record', 'Asks where to save, then records every camera and the captions.'],
            ['Leave', 'Ends the session and disconnects every camera, after asking you to confirm.']
        ]} />

        <CVHeading id="cameras" num="05"> Adding cameras </CVHeading>
        <p>Tap <UI>Add camera</UI> at any time, even in the middle of a session. The pairing screen shows:</p>
        <Shot name="hostAddCamera" />
        <ul>
            <li><strong>A QR code</strong> for phones to scan, and a short session code so people can tell sessions in neighbouring rooms apart.</li>
            <li><strong>A pairing link.</strong> Tap <UI>Copy pairing link</UI> to send it to a phone that cannot see the screen.</li>
            <li><UI>New code</UI> makes a fresh code. Codes stop working after ten minutes, and a new one is made every session.</li>
            <li><UI>Show code</UI> and hide code. While the code is hidden, no new camera can join. Useful when the iPad or Mac is on a projector.</li>
            <li><UI>Approve each camera</UI>. When on, you approve every phone before it joins. The phone waits and says so.</li>
            <li><UI>Shared venue Wi-Fi</UI>. Turn this on when you are on a school, office or event network used by many people. It limits the session to four cameras so the video stays smooth. On your own network you can have six.</li>
        </ul>
        <p>
            The count at the top shows how many cameras are connected out of the limit. If a phone drops off the Wi-Fi,
            it reconnects on its own and gets its old place back, as long as it returns within five minutes.
        </p>

        <CVHeading id="views" num="06"> Choosing a view </CVHeading>
        <Rows rows={[
            ['Grid', 'Every camera at once, each with its health readings. Best for small groups around a table, and for setting up.'],
            ['Auto', 'Fills the screen with the camera that has the speaker. It waits a moment between switches so the picture does not jump around. Best for lectures and panels.'],
            ['Two-Feed', <>One pinned camera beside the camera that has the speaker. Best with an interpreter. Pin a camera by long-pressing (or right-clicking) its tile and choosing <UI>Pin to Two-Feed</UI>. Needs at least two cameras.</>]
        ]} />
        <CVFigs layout="3">
            <Shot name="hostGrid" caption="Grid" />
            <Shot name="hostAuto" caption="Auto" />
            <Shot name="hostTwoFeed" caption="Two-Feed" />
        </CVFigs>
        <p>You can change view at any time. It does not interrupt recording or captions. Phones held upright are shown whole, not cropped.</p>

        <CVHeading id="speaker" num="07"> Who is speaking </CVHeading>
        <p>When someone talks, the iPad or Mac shows it in three ways:</p>
        <ul>
            <li>An <strong>amber frame</strong> around the speaker's face, with a name tag.</li>
            <li>An <strong>amber border</strong> around the camera that shows them.</li>
            <li>Their <strong>name at the start of each caption</strong> they speak.</li>
        </ul>
        <p>
            The highlight stays on the speaker through short pauses, so it does not flicker. If two people talk at once,
            the second gets a thinner outline. When a new person starts talking, the highlight moves to them in under a
            second.
        </p>
        <p>
            People are told apart by where they are, not by who they are. CaptiVision does not recognise faces or
            remember people between sessions.
        </p>
        <p className="cv-tip"><b>Tip</b>Detection works best when faces are clearly visible and well lit, and each phone is close enough to hear the people it films.</p>

        <CVHeading id="names" num="08"> Naming people </CVHeading>
        <p>Each new speaker gets a label such as <strong>Speaker 2</strong>. To give them a real name:</p>
        <ul>
            <li>Tap the badge next to their caption, or</li>
            <li>Open <UI>Speakers</UI>, find their photo, and tap <UI>Name</UI>.</li>
        </ul>
        <p>
            The new name appears on everything they have already said, as well as what they say next. The Speakers list
            shows who is speaking now, and a photo taken from the camera when it saw them.
        </p>
        <Shot name="hostSpeakers" />
        <p>
            To take someone off the list, choose <UI>Remove from list</UI>. Their photo and any box you
            drew for them are deleted. What they said stays in the transcript. Names and photos last only for the
            current session.
        </p>

        <CVHeading id="pin" num="09"> Pointing out a speaker yourself </CVHeading>
        <p>
            Sometimes the iPad or Mac cannot tell who is talking: a microphone covers someone's mouth, they turn away, or they
            sit behind a laptop. You can point them out yourself.
        </p>
        <ol>
            <li><strong>Drag across the person</strong> on any camera to draw a box around them.</li>
            <li>They become the speaker straight away, and their captions carry their name. You can name them right there.</li>
            <li>They stay pinned until you tap <UI>Release pin</UI>, and the iPad or Mac goes back to finding speakers on its own.</li>
        </ol>
        <p>
            Every box you draw is kept for the session. Turn on <UI>Show saved regions</UI> in the Speakers
            list to see them, then tap one to pin that person again the next time they speak. This makes a panel with
            the same seats quick to run.
        </p>

        <CVHeading id="captions" num="10"> Captions and speech </CVHeading>
        <p>
            Captions run along the bottom of the screen in the form <strong>[Name]: what they said</strong>. The newest line
            fills in as the person speaks. Earlier lines stay still, so they are easy to read. Tap <UI>Captions</UI> to show or hide them,
            change the text size, and choose how many lines stay on screen. You can also pinch the
            captions to resize them, or press ⌘+ and ⌘− on a keyboard.
        </p>
        <Shot name="hostCaptions" />
        <h3> Choosing a speech engine </h3>
        <p>Tap <UI>Speech engine</UI> to choose how captions are made. Every option runs on the iPad or Mac itself.</p>
        <Rows head={['Engine', 'Details']} rows={[
            ["Apple's built-in speech", 'The default on iPadOS 26 or macOS 26 or later. The system downloads your language once and keeps it.'],
            ['Whisper small', '190 MB download. The fastest, and good for clear speech in a quiet room. The default on earlier versions.'],
            ['Whisper medium', '539 MB download. More accurate, especially with accents and background noise, but slower than small.'],
            ['Whisper turbo', "574 MB download. Close to Whisper's largest model in accuracy, and quicker than medium. Needs the most memory."]
        ]} />
        <p>
            Each model downloads once and then works offline. Before a download, the iPad or Mac checks it has enough storage and
            memory; if a model will not fit, it tells you why. Tap <UI>Use</UI> to switch. You can switch in
            the middle of a session and captions carry on.
        </p>
        <p>
            <UI>Check captioning works</UI> plays an 11-second sample through the chosen engine and shows the
            result. Run it before people arrive.
        </p>
        <Shot name="hostSpeech" />

        <CVHeading id="tiles" num="11"> Controlling a camera from the iPad or Mac </CVHeading>
        <p>Each camera's tile shows how that phone is doing, so you rarely need to walk over to it.</p>
        <Rows rows={[
            ['Name and number', "Matches the name on the phone's own screen, so you know which phone is which."],
            ['Battery', 'Percentage, and whether it is charging.'],
            ['Microphone level', 'Shows the phone is hearing. A warning appears if it hears nothing.'],
            ['Level line', <>Appears only when the phone is tilted. <strong>FLAT</strong> means it is lying face up.</>],
            ['Delay', 'How many milliseconds the picture is behind the phone. Lower is better.'],
            ['FPS', 'Frames per second. The phone lowers this if it gets hot.'],
            ['Heat', 'Shown when the phone is warm or hot.']
        ]} />
        <h3> The camera menu </h3>
        <p>Long-press a tile (on a Mac, right-click it) to open its menu:</p>
        <ul>
            <li><UI>Pin to Two-Feed</UI> keeps this camera in the fixed side of Two-Feed.</li>
            <li><UI>Identify (flash screen)</UI> flashes the phone's screen so you can find it in the room.</li>
            <li><UI>Flip camera</UI> switches between the phone's front and back cameras.</li>
            <li><UI>Role</UI> sets the phone as a Camera or an Interpreter.</li>
            <li><UI>Zoom</UI> sets 1x, 2x, 3x or 5x.</li>
            <li><UI>Blank screen</UI> darkens the phone's screen to keep it cool. It keeps filming.</li>
            <li><UI>Remove camera</UI> disconnects the phone from the session.</li>
        </ul>
        <p>
            When you change a phone from the iPad or Mac, the phone shows a short note such as "Zoom set by host", so anyone near
            it knows what happened. If a phone cannot do what you asked, for example it has no zoom lens, the tile tells you.
        </p>
        <h3> Alerts </h3>
        <p>
            The iPad or Mac warns you about anything that could spoil the session: a low battery, a hot phone, a phone that hears
            nothing, or a phone without Do Not Disturb, which a call could interrupt. When several phones have the same
            problem, you get one alert that names them all.
        </p>

        <CVHeading id="recording" num="12"> Recording </CVHeading>
        <p>
            Tap <UI>Record</UI> and choose a folder. Nothing is recorded until you choose one. Each
            recording goes into a new folder containing:
        </p>
        <ul>
            <li>A video of each camera, with its own sound.</li>
            <li>A captions file that video players can show under the picture.</li>
            <li>A written transcript with names and times, as plain text and as a data file (JSON).</li>
            <li>The session log.</li>
        </ul>
        <p>
            While recording, every phone shows <strong>HOST RECORDING</strong>, so nobody is recorded without knowing. The
            iPad or Mac warns you when there are 15 minutes and 5 minutes of space left. If a recording stops suddenly, everything
            up to that point still plays. Tap <UI>Stop recording</UI> to finish.
        </p>

        <CVHeading id="log" num="13"> Session log </CVHeading>
        <p>The session log lists every camera joining and leaving, every recording, and every change you made, each with the time it happened. It is saved with each recording.</p>
        <Shot name="hostSessionLog" />

        <CVHeading id="leave" num="14"> Ending a session </CVHeading>
        <p>Tap <UI>Leave</UI> and confirm. Every phone is disconnected and goes back to normal.</p>

        {/* Camera */}
        <span className="cv-part"> On the iPhone: CaptiVision Client </span>

        <CVHeading id="join" num="15"> Joining a session </CVHeading>
        <p>Open CaptiVision Client. There are three ways in:</p>
        <ul>
            <li><UI>Scan the host's code</UI> and point the phone at the QR code on the iPad or Mac.</li>
            <li>Pick the session from the list under <UI>On this network</UI>.</li>
            <li><UI>Paste a pairing link</UI> copied from the iPad or Mac.</li>
        </ul>
        <CVFigs layout="4">
            <Shot name="cameraStart" caption="Start" />
            <Shot name="cameraScan" caption="Scan the code" />
            <Shot name="cameraPasteLink" caption="Paste a link" />
            <Shot name="cameraJoin" caption="Before you stream" />
        </CVFigs>
        <p>Before anything is sent, the phone shows exactly what it will do:</p>
        <ul>
            <li>Stream live video to the host.</li>
            <li>Stream microphone sound for captions.</li>
            <li>Stay awake, and may darken its screen to run cooler.</li>
            <li>Record nothing on this device.</li>
            <li>Send nothing to the internet.</li>
        </ul>
        <p>
            Check <UI>This camera's name</UI>, choose a <UI>Role</UI> (Camera, or Interpreter
            for a phone filming a sign language interpreter), and tap <UI>Start streaming</UI>. The first
            time, iOS asks for access to the camera, microphone and local network. All three are needed.
        </p>
        <h3> Do Not Disturb </h3>
        <p>
            An incoming call takes over the camera and cuts this angle for everyone relying on the captions. Each time
            streaming starts, the phone checks for Do Not Disturb. If it is off, tap <UI>Turn On Do Not Disturb</UI>:
            the app shows you where the switch is in Control Center and closes by itself once it is on. If it is already on,
            tap <UI>It's already on</UI>. You can also <UI>Skip for now</UI>, but the iPad or Mac will
            show that this phone could be interrupted.
        </p>
        <CVFigs layout="phones">
            <Shot name="cameraDnd" />
            <Shot name="cameraDndSteps" />
        </CVFigs>

        <CVHeading id="screen" num="16"> Reading the camera screen </CVHeading>
        <p>The phone's screen is designed to be read from across the room.</p>
        <Rows rows={[
            ['Connection', 'Whether the phone is connected, with its frame rate (FPS) and delay in milliseconds (MS).'],
            ['Battery', 'Percentage remaining.'],
            ['Microphone level', 'Moves with sound. Warns when sound is too loud and distorting, or when there is no sound at all.'],
            ['Level line', <>Helps you set the phone straight, then fades away. <strong>FLAT</strong> means the phone is lying face up.</>],
            ['Heat', 'Appears when the phone is warm, hot, or about to stop video.'],
            ['Exposure', 'Whether brightness is set from faces, from a spot you tapped, or locked.'],
            ['HOST RECORDING', 'The iPad or Mac is recording this session.'],
            ['INTERPRETER', 'This phone is the interpreter camera.']
        ]} />
        <Shot name="cameraLive" solo />

        <CVHeading id="framing" num="17"> Framing the shot </CVHeading>
        <ul>
            <li><strong>Zoom.</strong> Tap <UI>Zoom</UI> for a slider. The phone moves between its lenses smoothly as you zoom.</li>
            <li><strong>Front or back camera.</strong> Tap the flip button.</li>
            <li><strong>Brightness.</strong> The phone keeps faces bright on its own, even in front of a window. To set it for a particular spot, such as a whiteboard, tap that spot. Long-press to lock it there.</li>
            <li><strong>Any way up.</strong> The picture sent to the iPad or Mac stays level however you hold the phone.</li>
        </ul>

        <CVHeading id="cool" num="18"> Staying cool </CVHeading>
        <p>
            Filming for an hour warms a phone up. The screen is the biggest source of heat, so the phone can turn it
            off while it keeps filming. Tap the moon button to do this yourself. It also happens on its own when the
            phone gets warm. The screen then shows only a small note that capture is still active. Tap the screen to
            wake it.
        </p>
        <p>
            If the phone keeps getting hotter, it lowers its video quality step by step and tells the iPad or Mac, well before
            it would ever stop. To help it: take off thick cases, keep it out of direct sun, and avoid fast chargers
            during a session.
        </p>

        <CVHeading id="interpreter" num="19"> Interpreter camera </CVHeading>
        <p>
            Give the sign language interpreter their own phone and choose <strong>Interpreter</strong> as its role, when
            joining or later from the iPad or Mac. An interpreter camera is never cut away from, and it is the last to lose
            quality if the network gets busy. Frame the interpreter from head to hips, with room for their arms at full
            stretch. In Two-Feed view, pin the interpreter camera so it stays beside the speaker.
        </p>

        <CVHeading id="owner" num="20"> For the phone's owner </CVHeading>
        <p>If you have lent your phone to a session, you stay in control of it.</p>
        <ul>
            <li><strong>Mute.</strong> Tap the microphone button to stop sending sound. The iPad or Mac cannot unmute it.</li>
            <li><strong>Leave.</strong> Tap <UI>Leave session</UI> at any time. The iPad or Mac loses this camera, and your phone goes back to normal.</li>
            <li><strong>Nothing is kept.</strong> Nothing is recorded or stored on your phone, and nothing is sent to the internet. Leaving discards everything from the session and restores your auto-lock setting.</li>
        </ul>

        {/* Help */}
        <span className="cv-part"> Help </span>

        <CVHeading id="trouble" num="21"> Troubleshooting </CVHeading>
        <Rows head={['What you see', 'What to do']} rows={[
            ['"No sessions found yet"', 'Check the iPad or Mac has started a session and that both devices are on the same Wi-Fi.'],
            ['"The host is not reachable"', "Some guest and public networks stop devices from talking to each other. Ask the network's owner, or use a separate Wi-Fi router just for the session."],
            ['"That code has expired"', <>Tap <UI>New code</UI> on the iPad or Mac and scan again.</>],
            ['"The host is not showing its pairing code"', <>The code is hidden. Tap <UI>Show code</UI> on the iPad or Mac.</>],
            ['"Waiting for the host to approve this camera"', <><UI>Approve each camera</UI> is on. Approve the phone on the iPad or Mac.</>],
            ['"The session is full"', <>Remove a camera on the iPad or Mac. If you are on your own network, turn off <UI>Shared venue Wi-Fi</UI> to allow six.</>],
            ['"Running different versions"', 'Update both apps from the App Store.'],
            ['Local Network access is off', 'On the phone, open Settings, Privacy & Security, Local Network, and turn on CaptiVision Client.'],
            ['Do Not Disturb is on, but the phone still asks', <>In Settings, Focus, Focus Status, turn on Share Focus Status. Or tap <UI>It's already on</UI>.</>],
            ['No captions appear', <>Tap <UI>Speech engine</UI> and finish setting it up, then run <UI>Check captioning works</UI>. Check the microphone level on each tile, and that no phone is muted.</>],
            ['Nobody is highlighted', <>Make sure faces are visible and lit, and that phones are close enough to hear. You can always <Jump to="pin">draw a box</Jump> around the speaker.</>],
            ["A phone's screen went black", 'It darkened its screen to stay cool and is still filming. Tap it to wake it.'],
            ['The picture lags behind', 'Check the delay on the tile. Move the phones closer to the Wi-Fi router, use fewer cameras, or use a network just for the session.']
        ]} />

        <CVHeading id="pro" num="22"> CaptiVision Pro </CVHeading>
        <p>
            CaptiVision Client is always free. CaptiVision Host is free for your first seven days from the day you first
            open it. During that week, the <UI>Upgrade</UI> button shows how many days are left, and the plans
            appear once per session, shortly after the first camera connects.
        </p>
        <p>
            After the free week, starting a session needs CaptiVision Pro: <strong>{monthly} a month</strong>, starting with a
            one-week free trial, or <strong>{yearly} a year</strong>. These are the US prices; the App Store charges in your
            local currency, and prices vary by country or region. A session that is already running when the week ends
            is never cut off. Subscriptions are handled by Apple; manage or cancel them in Settings, under your name,
            then Subscriptions; on a Mac, in the App Store under your account. Already subscribed on another iPad or Mac? Use Restore on the plans screen. See the
            {' '}<Link to={`${BASE}/terms`}>Terms of Use</Link>.
        </p>

        <CVHeading id="access" num="23"> Languages and accessibility </CVHeading>
        <p>
            Both apps are available in English, Arabic, French, German, Spanish, Japanese, Korean, Chinese (Simplified),
            Portuguese (Brazil), Italian and Dutch, and follow your device's language.
        </p>
        <p>
            Both apps support larger text sizes, VoiceOver, the Large Content Viewer on icon buttons, and Reduce Motion.
            Every alert and change is shown on screen; nothing depends on sound alone.
        </p>

        <CVNext
            back={{ to: `${BASE}/get-started`, kicker: 'Back to', label: 'Get started' }}
            next={{ to: `${BASE}/privacy`, kicker: 'Read', label: 'Privacy Policy' }} />
    </>
}

export default page;
